import React from 'react';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const FooterSection = () => {
  const { language, direction } = useLanguage();
  const currentYear = new Date().getFullYear();

  const navigationItems = [
    {
      id: 'who-are-we',
      label: 'Who are we',
      labelAr: 'من نحن؟',
      labelTr: 'Biz kimiz',
      description: 'About Us',
      descriptionAr: 'من نحن',
      descriptionTr: 'Hakkımızda'
    },
    {
      id: 'get-involved',
      label: 'Register as participant',
      labelAr: 'التسجيل كمشارك',
      labelTr: 'Katılımcı olarak kayıt olun',
      description: 'Register',
      descriptionAr: 'التسجيل',
      descriptionTr: 'Kayıt'
    },
    {
      id: 'get-involved',
      label: 'Register as volunteer',
      labelAr: 'التسجيل كمتطوع',
      labelTr: 'Gönüllü olarak kayıt olun',
      description: 'Volunteer',
      descriptionAr: 'تطوع',
      descriptionTr: 'Gönüllü'
    }
  ];

  const footerContent = {
    ar: {
      title: "الذكرة السنوية لتحرير سورية",
      description: "احتفالاً بذكرى تحرير سورية وأملاً في مستقبل أفضل",
      quickLinks: "روابط سريعة",
      copyright: "جميع الحقوق محفوظة",
      developedBy: "تم تطوير هذا الموقع بواسطة"
    },
    en: {
      title: "Anniversary of the liberation of Syria",
      description: "Celebrating the anniversary of Syria's liberation and hope for a better future",
      quickLinks: "Quick Links",
      copyright: "All rights reserved",
      developedBy: "This website was developed by"
    },
    tr: {
      title: "Suriye'nin Kurtuluş Yıldönümü",
      description: "Suriye'nin kurtuluş yıldönümünü kutlayarak daha iyi bir gelecek umudunu taşıyoruz",
      quickLinks: "Hızlı Bağlantılar",
      copyright: "Tüm hakları saklıdır",
      developedBy: "Bu web sitesi tarafından geliştirilmiştir"
    }
  };

  const socialLinks = [
    {
      name: 'Facebook',
      icon: 'Facebook',
      url: 'https://www.facebook.com/profile.php?id=61584172431229'
    },
    {
      name: 'Instagram',
      icon: 'Instagram',
      url: 'https://www.instagram.com/the8thofdecember/'
    }
  ];

  const getLabel = (item) => {
    switch (language) {
      case 'ar': return item?.labelAr;
      case 'tr': return item?.labelTr;
      default: return item?.label;
    }
  };

  const content = footerContent?.[language];

  return (
    <footer id="footer" className="mt-auto bg-primary text-primary-foreground">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <div className={`grid grid-cols-2 gap-4 md:gap-8 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
          {/* Quick Links Section */}
          <div className="h-auto md:h-[300px] flex flex-col items-center">
            <h3
              className="text-base md:text-lg font-bold mb-3 md:mb-4 text-white text-center"
              style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}
            >
              {content?.quickLinks}
            </h3>
            <ul className="space-y-2 md:space-y-3 flex-1 w-full">
              {navigationItems?.map((item) => (
                <li key={item?.id}>
                  <a
                    href={`#${item?.id}`}
                    className="text-sm md:text-base text-white hover:opacity-80 transition-colors block min-h-[44px] flex items-center justify-center"
                    style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}
                  >
                    {getLabel(item)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Logo and Social Links Section */}
          <div className="h-auto md:h-[300px] flex flex-col items-center">
            <div className={`mb-4 md:mb-6 flex items-center gap-4 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <img
                alt="Logo"
                className="h-24 md:h-32 w-auto brightness-0 invert"
                src="/assets/logos/2-01.png"
                onError={(e) => {
                  // Fallback if logo doesn't exist
                  e.target.style.display = 'none';
                }}
              />
              <img
                alt="Logo"
                className="h-24 md:h-32 w-auto brightness-0 invert"
                src="/assets/logos/3.png"
                onError={(e) => {
                  // Fallback if logo doesn't exist
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <p
              className="text-sm md:text-base text-white mb-4 md:mb-6 leading-relaxed flex-1 overflow-hidden text-center"
              style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}
            >
              {content?.description}
            </p>
            <div className={`flex gap-4 justify-center ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
              {socialLinks?.map((social) => (
                <a
                  key={social?.name}
                  href={social?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110"
                  style={{ backgroundColor: 'rgb(31, 41, 55)' }}
                  aria-label={social?.name}
                >
                  <Icon
                    name={social?.icon}
                    size={18}
                    color="white"
                  />
                </a>
              ))}
            </div>
          </div>


        </div>

        {/* Bottom Footer */}
        <div className="border-t border-white/20 mt-4 md:mt-6 py-4 md:pt-4 h-auto md:h-[70px] flex flex-col justify-center">
          <p
            className="text-xs md:text-sm lg:text-base text-white text-center mb-2 px-4"
            style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}
          >
            © {currentYear} {content?.title}. {content?.copyright}.
          </p>
          <p
            className="text-xs md:text-sm text-white text-center px-4"
            style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}
          >
            {content?.developedBy}{' '}
            <a
              href="https://www.qk-dev.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:opacity-80 transition-colors font-medium"
            >
              QK-dev Software Services
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;